import React from 'react';
import {
  TrophyTwoTone,
  ArrowUpOutlined,
  ArrowDownOutlined,
  MinusOutlined,
  InfoCircleOutlined,
  DownloadOutlined,
} from '@ant-design/icons';
import {
  Col,
  Table as TableAntd,
  Button as ButtonAntd,
  Tooltip,
  Radio,
  Badge,
  Button,
  Space,
  Statistic,
} from 'antd';
import BoxSubItem from '~/components/BoxSubItem';
import Row from '~/components/Row';
import { formatPrice } from '~/Utils';
import './styles.css';

const priceIndicator = (newValue, value) => {
  if (value === 0 || newValue === 0) return null;
  const percentage = ((newValue - value) / value) * 100;
  if (percentage > 0) {
    return (
      <Statistic
        value={percentage}
        valueStyle={{ color: 'red', fontSize: '12px' }}
        precision={2}
        prefix={
          <>
            <ArrowUpOutlined />
          </>
        }
        suffix="%"
      />
    );
  }
  if (percentage < 0) {
    return (
      <Statistic
        value={percentage}
        precision={2}
        valueStyle={{ color: 'green', fontSize: '12px' }}
        prefix={
          <>
            <ArrowDownOutlined />
          </>
        }
        suffix="%"
      />
    );
  }
  return (
    <Statistic
      value={percentage}
      precision={2}
      valueStyle={{ color: 'gray', fontSize: '12px' }}
      prefix={
        <>
          <MinusOutlined />
        </>
      }
      suffix="%"
    />
  );
};

export function tableColumns(t, showType, winners, handleWinner, calcFinalDate) {
  return [
    {
      title: t('screens:price_quotes.subvendor_name'),
      key: 'subVendor.name',
      ellipsis: { showTitle: false },
      align: 'left',
      render: record => {
        return (
          <Row>
            <Col flex="30px">
              {winners.some(winner => winner.subVendorId === record.subVendor.id) ? (
                <Badge
                  count={winners.filter(winner => winner.subVendorId === record.subVendor.id).length}
                  offset={[2, -2]}
                  color="green"
                  showZero={false}
                  size="small"
                  overflowCount={99}
                >
                  <TrophyTwoTone twoToneColor="#52c41a" />
                </Badge>
              ) : null}
            </Col>
            <Col flex="auto">
              <Tooltip placement="topLeft" title={record.subVendor.companyName}>
                {record.subVendor.companyName}
              </Tooltip>
            </Col>
          </Row>
        );
      },
    },
    {
      title: t('screens:price_quotes.totalMaterialValue'),
      key: 'parentTotalMaterialValue',
      ellipsis: { showTitle: false },
      align: 'center',
      render: record => {
        // calc sum of all items material values times their quantity that laborType is equal to the current laborType and subVendor is equal to the current subVendor
        const sum = record.items
          ?.filter(
            item => item.laborType.id === showType,
            item => item.subVendor.id === record.subVendor.id
          )
          ?.reduce((acc, item) => acc + item.materialValue * item.quantity, 0);
        const ourSum = record.items
          ?.filter(
            item => item.laborType.id === showType,
            item => item.subVendor.id === record.subVendor.id
          )
          ?.reduce((acc, item) => acc + item.ownMaterialValue * item.ownQuantity, 0);
        return (
          <>
            <Row>
              <Col span={12}>Our</Col>
              <Col span={12}>Sent</Col>
            </Row>
            <Row>
              <Col span={12}>{formatPrice(ourSum)}</Col>
              <Col span={12}>{formatPrice(sum)}</Col>
            </Row>
            <Row>
              <Col span={12}></Col>
              <Col span={12}>{priceIndicator(sum, ourSum)}</Col>
            </Row>
          </>
        );
      },
    },
    {
      title: t('screens:price_quotes.totalLaborValue'),
      key: 'parentTotalLaborValue',
      ellipsis: { showTitle: false },
      align: 'center',
      render: record => {
        const sum = record.items
          ?.filter(
            item => item.laborType.id === showType,
            item => item.subVendor.id === record.subVendor.id
          )
          ?.reduce((acc, item) => acc + item.laborValue * item.quantity, 0);

        const ourSum = record.items
          ?.filter(
            item => item.laborType.id === showType,
            item => item.subVendor.id === record.subVendor.id
          )
          ?.reduce((acc, item) => acc + item.ownLaborValue * item.ownQuantity, 0);

        return (
          <>
            <Row>
              <Col span={12}>Our</Col>
              <Col span={12}>Sent</Col>
            </Row>
            <Row>
              <Col span={12}>{formatPrice(ourSum)}</Col>
              <Col span={12}>{formatPrice(sum)}</Col>
            </Row>
            <Row>
              <Col span={12}></Col>
              <Col span={12}>{priceIndicator(sum, ourSum)}</Col>
            </Row>
          </>
        );
      },
    },
    {
      title: t('screens:price_quotes.totalMaterialAndLaborValue'),
      key: 'parentTotalMaterialAndLaborValue',
      ellipsis: { showTitle: false },
      align: 'center',
      render: record => {
        const sum = record.items
          ?.filter(
            item => item.laborType.id === showType,
            item => item.subVendor.id === record.subVendor.id
          )
          ?.reduce((acc, item) => acc + (item.laborValue + item.materialValue) * item.quantity, 0);

        const ourSum = record.items
          ?.filter(
            item => item.laborType.id === showType,
            item => item.subVendor.id === record.subVendor.id
          )
          ?.reduce((acc, item) => acc + (item.ownLaborValue + item.ownMaterialValue) * item.ownQuantity, 0);

        return (
          <>
            <Row>
              <Col span={12}>Our</Col>
              <Col span={12}>Sent</Col>
            </Row>
            <Row>
              <Col span={12}>{formatPrice(ourSum)}</Col>
              <Col span={12}>{formatPrice(sum)}</Col>
            </Row>
            <Row>
              <Col span={12}></Col>
              <Col span={12}>{priceIndicator(sum, ourSum)}</Col>
            </Row>
          </>
        );
      },
    },
    {
      title: t('screens:price_quotes.dueDate'),
      key: 'price_quotes.dueDate',
      ellipsis: { showTitle: false },
      align: 'center',
      render: record => {
        // max dueDate
        const maxDueDate = record.items
          ?.filter(item => item.laborType.id === showType)
          ?.reduce((acc, item) => {
            if (acc === null) return item.dueDate;
            if (item.dueDate === null) return acc;
            return acc > item.dueDate ? acc : item.dueDate;
          }, null);

        return (
          <Tooltip placement="topLeft" title={`Estimate Date: ${calcFinalDate(maxDueDate)}`}>
            {`${maxDueDate} business days`}
          </Tooltip>
        );
      },
    },
    {
      title: t('screens:price_quotes.items.winner'),
      key: 'winner',
      align: 'center',
      width: '100px',
      ellipsis: { showTitle: false },
      render: record => {
        return (
          <>
            <Tooltip placement="topLeft" title={t('screens:price_quotes.mark_as_winner')}>
              <Radio
                checked={winners.find(
                  winner => winner.laborTypeId === showType && winner.subVendorId === record.subVendor.id
                )}
                onChange={() => handleWinner(showType, record.subVendor.id)}
              />
            </Tooltip>
          </>
        );
      },
    },
  ];
}

export function tableColumnsDetails(t, handleNoteModal) {
  return [
    {
      title: t('screens:price_quotes.items.description'),
      key: 'description',
      ellipsis: { showTitle: false },
      align: 'center',
      render: record => (
        <Tooltip placement="topLeft" title={record.description}>
          {record.description}
        </Tooltip>
      ),
    },
    {
      title: t('screens:price_quotes.items.area'),
      key: 'area.name',
      ellipsis: { showTitle: false },
      align: 'center',
      render: record => (
        <Tooltip placement="topLeft" title={record.area.name}>
          {record.area.name}
        </Tooltip>
      ),
    },
    {
      title: t('screens:price_quotes.items.unit'),
      dataIndex: 'unit',
      key: 'unit',
      width: '80px',
      ellipsis: { showTitle: false },
      align: 'center',
    },
    {
      title: t('screens:price_quotes.items.quantity'),
      key: 'quantity',
      width: '80px',
      ellipsis: {
        showTitle: true,
      },
      align: 'center',
      render: record => {
        const quantity = record.quantity;
        const ownQuantity = record.ownQuantity;
        return (
          <>
            <Row>
              <Col span={12}>Our</Col>
              <Col span={12}>Sent</Col>
            </Row>
            <Row>
              <Col span={12}>{ownQuantity}</Col>
              <Col span={12}>{quantity}</Col>
            </Row>
          </>
        );
      },
    },

    TableAntd.EXPAND_COLUMN,
    {
      title: t('screens:price_quotes.items.totalMaterialAndLabor'),
      key: 'totalMaterialAndLabor',
      width: '150px',
      ellipsis: { showTitle: false },
      align: 'center',
      render: record => {
        const total = (record.materialValue + record.laborValue) * record.quantity;
        const totalOwn = (record.ownMaterialValue + record.ownLaborValue) * record.quantity;
        // const isExpanded = expandedRowKeys.find(key => key.description === record.description);
        return {
          props: {
            // style: { background: isExpanded ? 'green' : 'red' },
          },
          children: (
            <>
              {formatPrice(total)}
              {priceIndicator(total, totalOwn)}
            </>
          ),
        };
      },
    },
    {
      title: t('screens:price_quotes.items.dueDate'),
      key: 'dueDate',
      width: '100px',
      ellipsis: { showTitle: false },
      align: 'center',
      render: record => <>{`${record.dueDate} business days`}</>,
    },
    {
      title: t('screens:price_quotes.items.notes'),
      key: 'note',
      ellipsis: { showTitle: false },
      align: 'center',
      width: '100px',
      render: record => (
        <Space>
          {record.note && record.note.length > 0 && (
            <Tooltip title={t('screens:price_quotes.items.viewSubcontractorNote')}>
              <Button type="link" icon={<InfoCircleOutlined />} onClick={() => handleNoteModal(record)} />
            </Tooltip>
          )}
          {record.subVendorFile && (
            <Tooltip title={t('screens:price_quotes.items.downloadSubcontractorFile')} placement="bottom">
              <ButtonAntd type="link" icon={<DownloadOutlined />} />
            </Tooltip>
          )}
          {!(record.note || record.subVendorFile) && <>N/A</>}
        </Space>
      ),
    },
  ];
}

export function tableColumnsDetailsExpanded(t) {
  return [
    {
      title: t('screens:price_quotes.items.materialValue'),
      key: 'materialValue',
      ellipsis: { showTitle: false },
      align: 'center',
      render: record => (
        <>
          <Row>
            <Col
              span={24}
              style={{
                fontWeight: 'bold',
                color: '#444',
                marginBottom: '5px',
                borderBottom: '1px solid #ddd',
                paddingBottom: '5px',
              }}
            >
              {t('screens:price_quotes.items.materialValue')}
            </Col>
          </Row>
          <Row>
            <Col span={12}>Our</Col>
            <Col span={12}>Sent</Col>
          </Row>
          <Row>
            <Col span={12}>{formatPrice(record.ownMaterialValue)}</Col>
            <Col span={12}>{formatPrice(record.materialValue)}</Col>
          </Row>
          <Row>
            <Col span={12}></Col>
            <Col span={12}>{priceIndicator(record.materialValue, record.ownMaterialValue)}</Col>
          </Row>
        </>
      ),
    },
    {
      title: t('screens:price_quotes.items.laborValue'),
      key: 'laborValue',
      ellipsis: { showTitle: false },
      align: 'center',
      render: record => {
        return {
          props: {
            style: { fontSize: '12px' },
          },
          children: (
            <>
              <Row>
                <Col
                  span={24}
                  style={{
                    fontWeight: 'bold',
                    color: '#444',
                    marginBottom: '5px',
                    borderBottom: '1px solid #ddd',
                    paddingBottom: '5px',
                  }}
                >
                  {t('screens:price_quotes.items.materialValue')}
                </Col>
              </Row>
              <Row>
                <Col span={12}>Our</Col>
                <Col span={12}>Sent</Col>
              </Row>
              <Row>
                <Col span={12}>{formatPrice(record.ownLaborValue)}</Col>
                <Col span={12}>{formatPrice(record.laborValue)}</Col>
              </Row>
              <Row>
                <Col span={12}></Col>
                <Col span={12}>{priceIndicator(record.laborValue, record.ownLaborValue)}</Col>
              </Row>
            </>
          ),
        };
      },
    },
    {
      title: t('screens:price_quotes.items.totalMaterial'),
      key: 'totalMaterial',
      ellipsis: { showTitle: false },
      align: 'center',
      render: record => {
        const totalMaterial = record.materialValue * record.quantity;
        const totalOwnMaterial = record.ownMaterialValue * record.ownQuantity;
        return (
          <>
            <Row>
              <Col
                span={24}
                style={{
                  fontWeight: 'bold',
                  color: '#444',
                  marginBottom: '5px',
                  borderBottom: '1px solid #ddd',
                  paddingBottom: '5px',
                }}
              >
                {t('screens:price_quotes.items.totalMaterial')}
              </Col>
            </Row>
            <Row>
              <Col span={12}>Our</Col>
              <Col span={12}>Sent</Col>
            </Row>
            <Row>
              <Col span={12}>{formatPrice(totalOwnMaterial)}</Col>
              <Col span={12}>{formatPrice(totalMaterial)}</Col>
            </Row>
            <Row>
              <Col span={12}></Col>
              <Col span={12}>{priceIndicator(totalMaterial, totalOwnMaterial)}</Col>
            </Row>
          </>
        );
      },
    },
    {
      title: t('screens:price_quotes.items.totalLabor'),
      key: 'totalLabor',
      ellipsis: { showTitle: false },
      align: 'center',
      render: record => {
        const totalLabor = record.laborValue * record.quantity;
        const totalOwnLabor = record.ownLaborValue * record.ownQuantity;
        return (
          <>
            <Row>
              <Col
                span={24}
                style={{
                  fontWeight: 'bold',
                  color: '#444',
                  marginBottom: '5px',
                  borderBottom: '1px solid #ddd',
                  paddingBottom: '5px',
                }}
              >
                {t('screens:price_quotes.items.totalLabor')}
              </Col>
            </Row>
            <Row>
              <Col span={12}>Our</Col>
              <Col span={12}>Sent</Col>
            </Row>
            <Row>
              <Col span={12}>{formatPrice(totalOwnLabor)}</Col>
              <Col span={12}>{formatPrice(totalLabor)}</Col>
            </Row>
            <Row>
              <Col span={12}></Col>
              <Col span={12}>{priceIndicator(totalLabor, totalOwnLabor)}</Col>
            </Row>
          </>
        );
      },
    },
    {
      title: t('screens:price_quotes.items.totalMaterialAndLabor'),
      key: 'totalMaterialAndLabor',
      ellipsis: { showTitle: false },
      align: 'center',
      render: record => {
        const total = (record.materialValue + record.laborValue) * record.quantity;
        const totalOwn = (record.ownMaterialValue + record.ownLaborValue) * record.ownQuantity;
        return {
          props: {
            style: { backgroundColor: '#f9f9f9' },
          },
          children: (
            <>
              <Row>
                <Col
                  span={24}
                  style={{
                    fontWeight: 'bold',
                    color: '#444',
                    marginBottom: '5px',
                    borderBottom: '1px solid #ddd',
                    paddingBottom: '5px',
                  }}
                >
                  {t('screens:price_quotes.items.totalMaterialAndLabor')}
                </Col>
              </Row>
              <Row>
                <Col span={12}>Our</Col>
                <Col span={12}>Sent</Col>
              </Row>
              <Row>
                <Col span={12}>{formatPrice(totalOwn)}</Col>
                <Col span={12}>{formatPrice(total)}</Col>
              </Row>
              <Row>
                <Col span={12}></Col>
                <Col span={12}>{priceIndicator(total, totalOwn)}</Col>
              </Row>
            </>
          ),
        };
      },
    },
  ];
}

export function tableSummary(t, selectedEstimateSubVendors, subvendorId, showType, loading) {
  // Filter the selectedEstimateSubVendors to get items for the specific subvendor and labor type
  if (!selectedEstimateSubVendors || selectedEstimateSubVendors.length === 0) return null;
  if (!subvendorId || showType === null) return null;
  const subvendorItems = selectedEstimateSubVendors
    .filter(i => i.subVendor.id === subvendorId)
    .filter(i => i.estimateItem.laborType.id !== showType);

  return subvendorItems.length !== 0 ? (
    <TableAntd.Summary.Row>
      <TableAntd.Summary.Cell colSpan={7}>
        <BoxSubItem title="Other labor types">
          <TableAntd
            bordered
            rowClassName={record => (record.estimateItem.laborType.id === showType ? 'table-row' : 'table-row-hidden')}
            rowKey="id"
            pagination={false}
            loading={loading}
            columns={[
              {
                title: t('screens:price_quotes.items.labor_type_name'),
                key: 'estimateItem.laborType.name',
                ellipsis: { showTitle: false },
                render: record => record.estimateItem.laborType.name,
              },
              {
                title: t('screens:price_quotes.items.description'),
                key: 'estimateItem.name',
                ellipsis: { showTitle: false },
                render: record => record.estimateItem.description,
              },
              {
                title: t('screens:price_quotes.items.area'),
                key: 'estimateItem.area.name',
                ellipsis: { showTitle: false },
                render: record => record.estimateItem.area.name,
              },
              {
                title: t('screens:price_quotes.items.unit'),
                key: 'estimateItem.unit',
                width: '80px',
                ellipsis: { showTitle: false },
                render: record => record.estimateItem.unit,
              },
              {
                title: t('screens:price_quotes.items.quantity'),
                key: 'estimateItem.quantity',
                width: '80px',
                ellipsis: { showTitle: false },
                render: record => record.estimateItem.quantity,
              },
              {
                title: t('screens:price_quotes.items.value'),
                key: 'estimateItem.value',
                width: '100px',
                ellipsis: { showTitle: false },
                render: record => <>{formatPrice(record.value)}</>,
              },
              {
                title: t('screens:price_quotes.items.dueDate'),
                key: 'estimateItem.dueDate',
                width: '100px',
                ellipsis: { showTitle: false },
                render: record => <>{`${record.dueDate} business days`}</>,
              },
              {
                title: t('screens:price_quotes.items.note'),
                key: 'estimateItem.note',
                ellipsis: { showTitle: false },
                render: record => record.note,
              },
            ]}
            dataSource={subvendorItems}
            showHeader={true}
            size="small"
          />
        </BoxSubItem>
      </TableAntd.Summary.Cell>
    </TableAntd.Summary.Row>
  ) : null;
}
